/**
 * Contenido de la pantalla de Planes (card 1053, diseño de Marina del 20/10:
 * https://claude.ai/design/p/0184dcd0-c6a4-4921-8b03-8103a0bb421a?file=Planes+Reuni%C3%B3n.dc.html).
 *
 * Cada texto es [key, default] para `t()`: se edita desde las traducciones sin deploy.
 *
 * Las filas que nombran una `feature` del registry del api (api/src/plans/registry.js)
 * toman el ✓/✕ de la matriz que devuelve `GET /plans`: es la misma que aplica el gate,
 * así la pantalla nunca promete algo que el api corta. Las demás (marketing, "SOON",
 * cosas que no se gatean) son fijas acá, por código de plan. Una fila con `feature` y `fixed`
 * usa `fixed` para el plan que no trae la feature en la matriz (la decisión comercial).
 */

// Códigos de plans.catalog. El nombre comercial sale de acá y no del catálogo: el
// diseño cambió los nombres (Básico → Esencial) y así se ajusta sin tocar Igor.
export const PLAN_NAMES = {
  basic: ["planes_plan_basic_name", "Esencial"],
  pro: ["planes_plan_pro_name", "Pro"],
  premium: ["planes_plan_premium_name", "Premium"],
};

// Plan del catálogo para el segmento de URL del formulario de suscripción
// (/suscripcion/pro, /premium, /esencial). Acepta el código del plan o su nombre visible,
// sin importar mayúsculas ni tildes; "escencial" también, por cómo se suele escribir.
// null si no corresponde a ningún plan del catálogo.
const SLUG_ALIASES = { escencial: "basic" };
const slugify = (value) => String(value || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();

export function planFromSlug(slug, plans = []) {
  const wanted = slugify(slug);
  if (!wanted) return null;
  const code = SLUG_ALIASES[wanted] || wanted;
  return (
    plans.find((plan) => plan.code === code) ||
    plans.find((plan) => slugify(PLAN_NAMES[plan.code]?.[1] || plan.name) === wanted) ||
    null
  );
}

export const PLAN_TAGLINES = {
  basic: ["planes_plan_basic_tagline", "Para empezar y mantener lo esencial"],
  pro: ["planes_plan_pro_tagline", "Para crecer sin límite de agenda"],
  premium: ["planes_plan_premium_tagline_beyond", "Para llevar la gestión de tu consultorio más allá."],
};

// Bullets de cada card. `intro` = "Todo lo de X, más:" (en Esencial, "Incluye:", para que
// las listas de las tres cards arranquen a la misma altura). La de agendas de Esencial usa
// el tope del plan ({{cap}}); sin tope cargado se cae a la versión sin número.
export const PLAN_HIGHLIGHTS = {
  basic: {
    intro: ["planes_basic_intro", "Incluye:"],
    items: [
      ["planes_basic_hce", "Historia clínica y recetas electrónicas"],
      ["planes_basic_agendas", "Hasta {{cap}} agendas", "planes_basic_agendas_no_cap", "Agendas limitadas"],
      ["planes_basic_crontu", "Perfil estándar en Crontu"],
    ],
  },
  pro: {
    intro: ["planes_pro_intro", "Todo lo de Esencial, más:"],
    items: [
      ["planes_pro_agendas", "Agendas ilimitadas y sesiones recurrentes"],
      ["planes_pro_telemedicine", "Telemedicina"],
      ["planes_pro_files", "Archivos adjuntos en HCE"],
      ["planes_pro_custom_forms", "Plantillas personalizadas por especialidad"],
      ["planes_pro_whatsapp", "WhatsApp semiautomático"],
      ["planes_pro_advanced_config", "Configuración avanzada de prácticas"],
    ],
  },
  premium: {
    intro: ["planes_premium_intro", "Todo lo de Pro, más:"],
    items: [
      ["planes_premium_cash_board", "Tablero de cobros"],
      ["planes_premium_caller", "Llamador de pacientes"],
      ["planes_premium_metrics_dashboard", "Dashboard con métricas"],
      ["planes_premium_arca", "Facturación ARCA"],
      ["planes_premium_support", "Soporte prioritario y capacitación personalizada"],
    ],
  },
};

const ALL = { basic: "yes", pro: "yes", premium: "yes" };
const SOON = { basic: "soon", pro: "soon", premium: "soon" };
const ONLY_PREMIUM = { basic: "no", pro: "no", premium: "yes" };

// Bloque "Inteligencia artificial" de las cards.
export const AI_ROWS = [
  { label: ["planes_ai_hce_summary", "Resumen de historia clínica"], feature: "hce.ai_summary" },
  { label: ["planes_ai_meet_recording", "Grabación y transcripción de videollamada"], feature: "meet.ai_summary" },
  { label: ["planes_ai_consult_recording", "Grabación y transcripción de la consulta"], feature: "escriba.recording" },
  { label: ["planes_ai_copilot", "Copiloto IA"], fixed: { basic: "no", pro: "no", premium: "yes" } },
  { label: ["planes_ai_assistant", "Asistente IA"], fixed: { basic: "no", pro: "no", premium: "yes" } },
];

// Tabla "Qué podés hacer con cada plan". Valor de celda:
//   "yes" | "no" | "soon"  → ícono / pastilla
//   [key, default]          → texto (ej. "Hasta 5", "2%")
// `extra` = aclaración gris al lado del nombre ("(con cargo extra)").
// `limit` = la celda sale del cupo del plan en `GET /plans` (agendaCap).
export const COMPARISON = [
  {
    title: ["planes_section_agenda", "Agenda y turnos"],
    rows: [
      { label: ["planes_row_manual_events", "Turnos manuales"], fixed: ALL },
      { label: ["planes_row_agendas", "Agendas"], limit: "agendaCap" },
      { label: ["planes_row_sessions_recurring", "Sesiones recurrentes"], feature: "events.sessions" },
      { label: ["planes_row_telemedicine", "Telemedicina"], feature: "events.telemedicine" },
      { label: ["planes_row_advanced_config", "Config. avanzada de prácticas"], feature: "events.advanced_config" },
    ],
  },
  {
    title: ["planes_section_hce", "Historia clínica"],
    rows: [
      { label: ["planes_row_evolutions", "Evoluciones, diagnósticos y tratamientos"], fixed: ALL },
      { label: ["planes_row_prescriptions", "Recetas electrónicas"], fixed: ALL },
      { label: ["planes_row_single_export", "Exportación individual"], fixed: ALL },
      { label: ["planes_row_files", "Archivos adjuntos"], feature: "hce.files" },
      // Con check en los tres planes (Maru 31/10).
      { label: ["planes_row_bulk_export", "Exportación masiva"], feature: "reports.bulk_export", fixed: ALL },
      // Sólo Pro y Premium (decisión de Directorio, Juan Cruz 02/11).
      { label: ["planes_row_custom_forms", "Plantillas personalizadas por especialidad"], feature: "hce.custom_forms", fixed: { basic: "no", pro: "yes", premium: "yes" } },
      { label: ["planes_row_osde_orders", "Integración de órdenes médicas con OSDE"], fixed: SOON },
    ],
  },
  {
    title: ["planes_section_ai", "Inteligencia artificial"],
    ai: true,
    rows: [
      { label: ["planes_row_ai_hce_summary", "Resumen de historia clínica"], feature: "hce.ai_summary" },
      { label: ["planes_row_ai_consult_recording", "Grabación y transcripción de la consulta"], feature: "escriba.recording" },
      { label: ["planes_row_ai_meet_recording", "Grabación y transcripción de videollamada"], feature: "meet.ai_summary" },
      { label: ["planes_row_ai_copilot", "Copiloto IA"], fixed: ONLY_PREMIUM },
      { label: ["planes_row_ai_assistant", "Asistente IA"], fixed: ONLY_PREMIUM },
    ],
  },
  {
    title: ["planes_section_crontu", "Crontu"],
    rows: [
      { label: ["planes_row_crontu_profile", "Perfil estándar Crontu"], fixed: ALL },
      { label: ["planes_row_ai_agent", "Agente de turnos con IA, 24 horas"], fixed: ALL },
      { label: ["planes_row_patient_files", "Envío de archivos a pacientes"], fixed: ALL },
      { label: ["planes_row_payment_rules", "Reglas de cobro"], fixed: ALL },
    ],
  },
  {
    title: ["planes_section_notifications", "Notificaciones"],
    rows: [
      { label: ["planes_row_emails", "Correos electrónicos"], fixed: ALL },
      { label: ["planes_row_whatsapp_semi", "WhatsApp semiautomático"], feature: "whatsapp.semi_auto" },
      { label: ["planes_row_whatsapp_auto", "WhatsApp automático"], extra: ["planes_extra_charge", "(con cargo extra)"], fixed: ALL },
    ],
  },
  {
    title: ["planes_section_payments", "Cobros"],
    rows: [
      { label: ["planes_row_mp", "Integración Mercado Pago"], fixed: ALL },
      {
        label: ["planes_row_mp_fee", "Fee Mercado Pago"],
        fixed: {
          basic: ["planes_mp_fee_basic", "2%"],
          pro: ["planes_mp_fee_pro", "1%"],
          premium: ["planes_mp_fee_premium", "0,5%"],
        },
      },
      { label: ["planes_row_arca", "Facturación ARCA"], feature: "arca.invoicing" },
    ],
  },
  {
    title: ["planes_section_apps", "Apps"],
    rows: [
      { label: ["planes_row_cash_board", "Tablero de cobros"], fixed: ONLY_PREMIUM },
      { label: ["planes_row_caller", "Llamador de pacientes"], fixed: ONLY_PREMIUM },
      { label: ["planes_row_metrics_dashboard", "Dashboard de métricas"], fixed: ONLY_PREMIUM },
    ],
  },
  {
    title: ["planes_section_api", "API"],
    rows: [
      // Sólo Premium: el api la corta con apps.access (services/public-api, authenticate).
      { label: ["planes_row_api_read", "API pública de lectura"], feature: "apps.access", fixed: ONLY_PREMIUM },
      { label: ["planes_row_api_write", "API escritura clínica + webhooks"], fixed: ONLY_PREMIUM },
    ],
  },
  {
    title: ["planes_section_support", "Soporte"],
    rows: [
      { label: ["planes_row_help_center", "Centro de ayuda y videotutoriales"], fixed: ALL },
      {
        label: ["planes_row_support", "Soporte"],
        fixed: { basic: "yes", pro: "yes", premium: ["planes_priority_support", "Soporte prioritario"] },
      },
      { label: ["planes_row_training", "Capacitación personalizada"], fixed: ONLY_PREMIUM },
    ],
  },
];

// Funciones que mide el api (trial-usage, api#587): "Lo que usaste en la prueba" del banner y el
// "no vas a poder seguir usando" de las cards. El api manda `usage` por feature key; `amount`
// es el dato del banner ("4 videollamadas") y `usage` el de las cards ("Usaste 4 videollamadas").
export const TRIAL_USAGE = [
  { key: "telemedicine", feature: "events.telemedicine", label: ["planes_row_telemedicine", "Telemedicina"], amount: ["planes_usage_telemedicine", "{{count}} videollamadas"], usage: ["planes_used_telemedicine", "Usaste {{count}} videollamadas"] },
  { key: "files", feature: "hce.files", label: ["planes_row_files_hce", "Archivos adjuntos en HCE"], amount: ["planes_usage_files", "{{count}} archivos"], usage: ["planes_used_files", "Usaste {{count}} archivos"] },
  { key: "sessions", feature: "events.sessions", label: ["planes_row_sessions_long", "Sesiones (turnos recurrentes)"], amount: ["planes_usage_sessions", "{{count}} sesiones"], usage: ["planes_used_sessions", "Usaste {{count}} sesiones"] },
  { key: "aiSummaries", feature: "hce.ai_summary", label: ["planes_row_ai_hce_summary_long", "Resumen de historia clínica con IA"], amount: ["planes_usage_ai_summaries", "{{count}} resúmenes"], usage: ["planes_used_ai_summaries", "Usaste {{count}} resúmenes"] },
  { key: "arcaInvoices", feature: "arca.invoicing", label: ["planes_row_arca", "Facturación ARCA"], amount: ["planes_usage_arca", "{{count}} facturas"], usage: ["planes_used_arca", "Usaste {{count}} facturas"] },
];

const usedCount = (usage, feature) => {
  const count = usage?.[feature];
  return Number.isInteger(count) && count > 0 ? count : 0;
};

// Lo que se deja de tener al pasar de `reference` (el plan en prueba) a `plan`, entre las
// funciones medidas. El ✓/✕ sale de la matriz de `GET /plans`, como la tabla.
// Con `usage` (trial-usage) se listan sólo las usadas, con su cantidad; sin él, todas.
export function lostFeatures(plan, reference, usage = null) {
  return TRIAL_USAGE.filter((item) => {
    const row = { feature: item.feature };
    if (cellValue(row, reference) !== "yes" || cellValue(row, plan) !== "no") return false;
    return !usage || usedCount(usage, item.feature) > 0;
  }).map((item) => ({ ...item, count: usage ? usedCount(usage, item.feature) : null }));
}

// "Lo que usaste en la prueba" (diseño "Planes Trial"): las funciones usadas, con la cantidad y
// el plan más barato que la incluye (la pastilla PRO / PREMIUM).
export function trialUsageItems(usage, plans) {
  if (!usage) return [];
  const sorted = [...(plans || [])].sort((a, b) => a.rank - b.rank);
  return TRIAL_USAGE.filter((item) => usedCount(usage, item.feature) > 0).map((item) => ({
    ...item,
    count: usedCount(usage, item.feature),
    plan: sorted.find((plan) => cellValue({ feature: item.feature }, plan) === "yes") ?? null,
  }));
}

// Lo que el consultorio deja de tener al pedir la baja a `plan` desde `current` (modal
// "Cambiar a <plan>", diseño "Flujos comerciales", flujo 3). En orden de importancia; el
// ✓/✕ sale de la matriz de `GET /plans`. Varias features en un ítem = se pierde alguna.
// El diseño lista lo que el consultorio USA y, si no usó nada, las más importantes. El uso
// sale de `trial-usage` (api#587: la prueba o, sin prueba reciente, los últimos 30 días),
// que mide telemedicina, sesiones, archivos, resumen IA y ARCA; el resto no se puede marcar
// como usado.
export const DOWNGRADE_LOSSES = [
  { features: ["events.telemedicine", "hce.files"], label: ["planes_loss_telemedicine_files", "Telemedicina y archivos adjuntos en HCE"] },
  { features: ["hce.ai_summary"], label: ["planes_loss_ai_hce_summary", "Resumen de historia clínica con IA"] },
  { features: ["whatsapp.semi_auto"], label: ["planes_loss_whatsapp_semi", "WhatsApp semiautomático"] },
  { features: ["events.advanced_config"], label: ["planes_loss_advanced_config", "Configuración avanzada de prácticas"] },
  { features: ["arca.invoicing"], label: ["planes_loss_arca", "Facturación ARCA"] },
  { features: ["escriba.recording"], label: ["planes_loss_consult_recording", "Grabación y transcripción de la consulta con IA"] },
  { features: ["meet.ai_summary"], label: ["planes_loss_meet_recording", "Grabación y transcripción de videollamada con IA"] },
];
const MAX_DOWNGRADE_LOSSES = 4;

// Ítems del recuadro: primero el tope de agendas si el plan nuevo lo tiene y el actual no
// ({ agendas: { cap, count } }), y después hasta completar 4 funciones que se pierden.
// `usage` = `usage` de trial-usage ({ <feature key>: n | null }): si alguna de las que se
// pierden se usó, van sólo las usadas; si no (o sin datos), las más importantes.
export function downgradeLosses(plan, current, activeAgendas, usage = null) {
  const items = [];
  const cap = plan?.agendaCap?.value;
  if (current && Number.isInteger(cap) && !Number.isInteger(current.agendaCap?.value)) {
    items.push({ key: "agendas", agendas: { cap, count: Number.isInteger(activeAgendas) ? activeAgendas : null } });
  }
  const lost = DOWNGRADE_LOSSES.filter((loss) =>
    loss.features.some((feature) => {
      const row = { feature };
      return current && cellValue(row, current) === "yes" && cellValue(row, plan) === "no";
    })
  );
  const used = lost.filter((loss) => loss.features.some((feature) => usedCount(usage, feature) > 0));
  for (const loss of used.length ? used : lost) {
    if (items.length >= MAX_DOWNGRADE_LOSSES) break;
    items.push({ key: loss.label[0], label: loss.label });
  }
  return items;
}

// "Qué incluye" del formulario de suscripción con plan (caso 5, link de pago): lo que el
// plan suma respecto del anterior (`previous`, null para el más barato → todo lo incluido),
// por sección de la tabla. Cada ítem es { label: [key, default, opts?], extra?, ai }.
// Textos que cambian entre planes (fee de MP, soporte) entran con su valor.
export function planIncludes(plan, previous) {
  const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);
  return COMPARISON.map((section) => {
    const items = [];
    for (const row of section.rows) {
      const value = cellValue(row, plan);
      const before = previous ? cellValue(row, previous) : null;
      if (row.limit) {
        // "Ilimitadas" sólo con el tope configurado en null, no sin configurar.
        const unlimited = !!plan[row.limit] && plan[row.limit].value == null;
        if (unlimited && (!previous || Number.isInteger(previous[row.limit]?.value))) {
          items.push({ label: ["planes_includes_unlimited_agendas", "Agendas ilimitadas"] });
        }
      } else if (value === "yes") {
        if (before !== "yes") items.push({ label: row.label, extra: row.extra, ai: !!section.ai });
      } else if (Array.isArray(value) && !same(value, before)) {
        items.push({
          label: row.label[0] === "planes_row_mp_fee"
            ? ["planes_includes_mp_fee", "Fee de Mercado Pago del {{fee}}", { feeKey: value }]
            : value,
        });
      }
    }
    return { title: section.title, items };
  }).filter((section) => section.items.length);
}

// Valor de una fila para un plan del catálogo: la matriz del api si la fila nombra una
// feature (y el plan la trae), si no lo fijo. Plan desconocido en lo fijo → "no".
export function cellValue(row, plan) {
  if (row.limit) {
    // El catálogo trae el cupo como { value: N|null } o null (sin configurar). Sin
    // configurar no es "sin tope": no se promete nada.
    const limit = plan[row.limit];
    if (!limit) return ["planes_cell_unknown", "—"];
    return Number.isInteger(limit.value)
      ? ["planes_cell_up_to", "Hasta {{cap}}", { cap: limit.value }]
      : ["planes_cell_unlimited", "Ilimitadas"];
  }
  if (row.feature && plan.features?.[row.feature]) {
    return plan.features[row.feature].enabled ? "yes" : "no";
  }
  return row.fixed?.[plan.code] ?? "no";
}
